import { X } from 'lucide-react';
import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

const ImageCropModal = ({ isOpen, onClose, imageSrc, onCropComplete: onCropCompleteCallback }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropChange = useCallback((crop) => {
        setCrop(crop);
    }, []);

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const onZoomChange = useCallback((zoom) => {
        setZoom(zoom);
    }, []);

    const getCroppedImg = async (imageSrc, croppedAreaPixels) => {
        const image = new Image();
        image.src = imageSrc;

        return new Promise((resolve) => {
            image.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.width = croppedAreaPixels.width;
                canvas.height = croppedAreaPixels.height;

                ctx.drawImage(
                    image,
                    croppedAreaPixels.x,
                    croppedAreaPixels.y,
                    croppedAreaPixels.width,
                    croppedAreaPixels.height,
                    0,
                    0,
                    croppedAreaPixels.width,
                    croppedAreaPixels.height
                );

                canvas.toBlob((blob) => {
                    resolve(blob);
                }, 'image/jpeg', 0.9);
            };
        });
    };

    const handleCropSave = async () => {
        if (croppedAreaPixels) {
            try {
                const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
                const croppedImageUrl = URL.createObjectURL(croppedImage);
                onCropCompleteCallback(croppedImageUrl, croppedImage);
                onClose();
            } catch (error) {
                console.error('Error cropping image:', error);
            }
        }
    };

    // Handle both click and touch events for mobile compatibility
    const handleButtonClick = (action) => {
        if (action === 'close') {
            onClose();
        } else if (action === 'crop') {
            handleCropSave();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
        >
            <div
                className="bg-white rounded-lg p-4 md:p-6 max-w-2xl w-full mx-2 md:mx-4 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between relative items-center mb-4">
                    <h3 className="text-lg font-semibold">Crop Image</h3>
                    <button
                        onClick={() => handleButtonClick('close')}
                        onTouchEnd={() => handleButtonClick('close')}
                        className="absolute cursor-pointer top-0 right-0 text-gray-400 hover:text-gray-600 z-10 p-2"
                        style={{ minHeight: '44px', minWidth: '44px' }} // Mobile touch target size
                    >
                        <span className="bg-gray-400 w-6 h-6 rounded-full flex items-center justify-center">
                            <X className="w-4 h-4 text-white" />
                        </span>
                    </button>
                </div>

                <div className="relative h-64 md:h-96 mb-4">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={onCropChange}
                        onCropComplete={onCropComplete}
                        onZoomChange={onZoomChange}
                        showGrid={true}
                        objectFit="contain"
                    />
                </div>

                <div className="flex items-center gap-4 mb-4">
                    <label className="text-sm font-medium">Zoom:</label>
                    <input
                        type="range"
                        min="1"
                        max="3"
                        step="0.1"
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        className="flex-1 h-2 rounded-lg accent-[#21212136]"
                    />
                    <span className="text-sm text-gray-600">{zoom.toFixed(1)}x</span>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        onClick={() => handleButtonClick('close')}
                        onTouchEnd={() => handleButtonClick('close')}
                        className="px-4 py-3 text-gray-600 border border-gray-300 md:text-sm text-xs rounded-xl w-full sm:w-auto"
                        style={{ minHeight: '44px' }} // Mobile touch target size
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => handleButtonClick('crop')}
                        onTouchEnd={() => handleButtonClick('crop')}
                        className="px-4 py-3 bg-[#212121] text-white md:text-sm text-xs rounded-xl w-full sm:w-auto"
                        style={{ minHeight: '44px' }} // Mobile touch target size
                    >
                        Crop & Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropModal;
